import React, { useEffect } from 'react';
import { Box, Typography, Divider } from '@mui/material';
import { CalendarToday as CalendarIcon } from '@mui/icons-material';

import { enumToLabel } from '../../services/excel-table.service';
import { formatDateToYYMMDD } from '../../components/materials/datepickers/datepicker-in-excel';

import type { CreatePlanRequest } from '../../types/plan.types';

import { MobileCardContainer } from '@/components/mobile/mobile-card-container';
import { MobileSectionLabelStyles, MobileSectionValueStyles } from '@/components/styles';

// Shared style constants
const FONT_FAMILY = '"Noto Sans JP", sans-serif';

const statusBadgeTypographyStyle = {
  fontSize: '12px',
  fontWeight: 500,
  lineHeight: '16px',
  letterSpacing: '0.2px',
  color: '#FFFFFF',
  fontFamily: FONT_FAMILY,
  whiteSpace: 'nowrap' as const,
};

const dateHeaderTypographyStyle = {
  fontSize: '16px',
  fontWeight: 400,
  lineHeight: '24px',
  letterSpacing: '0.5px',
  color: '#1A1C20',
  fontFamily: FONT_FAMILY,
};

// Box styles
const rowBoxStyle = {
  display: 'flex',
  gap: '16px',
  minHeight: '20px',
};

const labelContainerBoxStyle = {
  width: '104px',
  flexShrink: 0,
};

const valueContainerBoxStyle = {
  flex: 1,
  overflowWrap: 'break-word',
  wordBreak: 'break-word',
};

const dividerStyle = {
  borderColor: '#C4C6D0',
};

// Helper component for simple field row (label + value)
interface FieldRowProps {
  label: string;
  value: string | number | null | undefined;
  valueFormatter?: (value: string | number | null | undefined) => string | number;
}

const FieldRow: React.FC<FieldRowProps> = ({ label, value, valueFormatter }) => {
  const displayValue = valueFormatter ? valueFormatter(value) : value || '-';
  return (
    <Box sx={rowBoxStyle}>
      <Box sx={labelContainerBoxStyle}>
        <MobileSectionLabelStyles>{label}</MobileSectionLabelStyles>
      </Box>
      <Box sx={valueContainerBoxStyle}>
        <MobileSectionValueStyles>{displayValue}</MobileSectionValueStyles>
      </Box>
    </Box>
  );
};

// Reusable component for grouped field row 1 (main label + first child label + value)
interface GroupedFieldRow1Props {
  mainLabel: string;
  firstChildLabel: string;
  value: string | number | null | undefined;
}

const GroupedFieldRow1: React.FC<GroupedFieldRow1Props> = ({
  mainLabel,
  firstChildLabel,
  value,
}) => (
  <Box sx={rowBoxStyle}>
    <Box sx={{ ...labelContainerBoxStyle, position: 'relative' }}>
      <MobileSectionLabelStyles>{mainLabel}</MobileSectionLabelStyles>
      <MobileSectionLabelStyles sx={{ position: 'absolute', left: '68px', top: 0 }}>
        {firstChildLabel}
      </MobileSectionLabelStyles>
    </Box>
    <Box sx={valueContainerBoxStyle}>
      <MobileSectionValueStyles>{value || '-'}</MobileSectionValueStyles>
    </Box>
  </Box>
);

// Reusable component for grouped field row 2 (second child label + value)
interface GroupedFieldRow2Props {
  secondChildLabel: string;
  value: string | number | null | undefined;
}

const GroupedFieldRow2: React.FC<GroupedFieldRow2Props> = ({ secondChildLabel, value }) => (
  <Box sx={rowBoxStyle}>
    <Box sx={labelContainerBoxStyle}>
      <MobileSectionLabelStyles sx={{ paddingLeft: '68px' }}>
        {secondChildLabel}
      </MobileSectionLabelStyles>
    </Box>
    <Box sx={valueContainerBoxStyle}>
      <MobileSectionValueStyles>{value || '-'}</MobileSectionValueStyles>
    </Box>
  </Box>
);

interface PlanDeliveryItemProps {
  delivery: CreatePlanRequest;
}

/**
 * Individual plan delivery item component
 * Displays plan delivery details matching Figma design
 */
const PlanDeliveryItem: React.FC<PlanDeliveryItemProps> = ({ delivery }) => {
  // Get status badge color based on orderStatus
  const getStatusColor = (orderStatus: number): string => {
    // EPlanOrderStatus.NOT_YET = 2 (交渉中) = Red
    // EPlanOrderStatus.DONE = 3 (手配済) = Blue
    return orderStatus === 3 ? '#425E91' : '#B00020'; // Blue for arranged (手配済), Red for negotiating (交渉中)
  };

  const statusLabel = enumToLabel.orderStatus(delivery.orderStatus);
  const statusColor = getStatusColor(delivery.orderStatus);

  let quantityandSuffix;
  if (delivery.quantity && delivery.quantitySuffix) {
    quantityandSuffix =
      delivery.quantity + ' ' + enumToLabel.quantitySuffix(delivery.quantitySuffix);
  } else {
    quantityandSuffix = delivery.quantity;
  }

  let timePossibleWithRemarks;
  if (delivery.timePossible && delivery.timePossibleRemarks) {
    timePossibleWithRemarks = delivery.timePossible + delivery.timePossibleRemarks;
  } else {
    timePossibleWithRemarks = delivery.timePossible;
  }

  return (
    <Box
      sx={{
        borderLeft: '2px solid #0047BA',
        paddingLeft: '18px',
        paddingRight: '16px',
        paddingY: '12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      }}
    >
      {/* プラント */}
      <FieldRow label='プラント' value={delivery.customerSupplier?.abbreviationName} />

      {/* 配合・商品 */}
      <FieldRow label='配合・商品' value={delivery.formulationProduct} />

      {/* 打設箇所 */}
      <FieldRow label='打設箇所' value={delivery.placeToPlace} />

      {/* 時間 - with sub-fields */}
      <GroupedFieldRow1 mainLabel='時間' firstChildLabel='希望' value={delivery.timeHope} />
      <GroupedFieldRow2 secondChildLabel='可能' value={timePossibleWithRemarks} />

      {/* 車両サイズ */}
      <FieldRow
        label='車両サイズ'
        value={delivery.vehicleSize}
        valueFormatter={(val) => enumToLabel.vehicleSize(val as number)}
      />

      {/* 数量 */}
      <FieldRow
        label='数量'
        value={quantityandSuffix}
        valueFormatter={(val) => (val !== null && val !== undefined ? val : '-')}
      />

      {/* 先行材 - with sub-fields */}
      {/* Row 1: Main label + First child label + Value 1 */}
      <Box sx={rowBoxStyle}>
        <Box sx={{ ...labelContainerBoxStyle, position: 'relative' }}>
          <MobileSectionLabelStyles>先行材</MobileSectionLabelStyles>
          <MobileSectionLabelStyles sx={{ position: 'absolute', left: '68px', top: 0 }}>
            種類
          </MobileSectionLabelStyles>
        </Box>
        <Box sx={valueContainerBoxStyle}>
          <MobileSectionValueStyles>
            {delivery.precedingMaterialType
              ? enumToLabel.materialType(delivery.precedingMaterialType)
              : '-'}
          </MobileSectionValueStyles>
        </Box>
      </Box>
      {/* Row 2: Second child label + Value 2 */}
      <Box sx={rowBoxStyle}>
        <Box sx={labelContainerBoxStyle}>
          <MobileSectionLabelStyles
            sx={{
              paddingLeft: '68px', // Fixed padding to align with other sections
            }}
          >
            数量
          </MobileSectionLabelStyles>
        </Box>
        <Box sx={valueContainerBoxStyle}>
          <MobileSectionValueStyles>
            {delivery.precedingMaterialQuantity !== null &&
            delivery.precedingMaterialQuantity !== undefined
              ? delivery.precedingMaterialQuantity
              : '-'}
          </MobileSectionValueStyles>
        </Box>
      </Box>

      {/* ピッチ - with sub-fields */}
      <GroupedFieldRow1 mainLabel='ピッチ' firstChildLabel='希望' value={delivery.pitchHope} />
      <GroupedFieldRow2 secondChildLabel='可能' value={delivery.pitchPossible} />

      {/* 打設方法 */}
      <FieldRow
        label='打設方法'
        value={delivery.placingMethod}
        valueFormatter={(val) =>
          val !== null && val !== undefined ? enumToLabel.placingMethod(val as number) : '-'
        }
      />

      {/* 立会会社 */}
      <FieldRow label='立会会社' value={delivery.witnessCompany?.name} />

      {/* 立会者 */}
      <FieldRow label='立会者' value={delivery.witnessName} />

      {/* 立会者連絡先 */}
      <FieldRow label='立会者連絡先' value={delivery.witnessTel} />

      {/* ステータス - Badge */}
      <Box sx={{ ...rowBoxStyle, alignItems: 'center' }}>
        <Box sx={labelContainerBoxStyle}>
          <MobileSectionLabelStyles>ステータス</MobileSectionLabelStyles>
        </Box>
        <Box sx={valueContainerBoxStyle}>
          <Box
            sx={{
              backgroundColor: statusColor,
              borderRadius: '1000px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              paddingX: '12px',
              paddingY: '6px',
              height: 'auto',
            }}
          >
            <Typography sx={statusBadgeTypographyStyle}>{statusLabel}</Typography>
          </Box>
        </Box>
      </Box>

      {/* 備考 */}
      <Box sx={{ display: 'flex', gap: '16px', minHeight: '20px' }}>
        <Box sx={{ ...labelContainerBoxStyle, minHeight: '20px' }}>
          <MobileSectionLabelStyles>備考</MobileSectionLabelStyles>
        </Box>
        <Box sx={valueContainerBoxStyle}>
          <MobileSectionValueStyles>{delivery.remarks || '-'}</MobileSectionValueStyles>
        </Box>
      </Box>

      {/* GC向け備考 */}
      <FieldRow label='GC向け備考' value={delivery.remarksForClient} />
    </Box>
  );
};

interface PlanDeliveryDateGroupProps {
  date: string;
  deliveries: CreatePlanRequest[];
}

/**
 * Date group component showing all deliveries for a specific date
 * Only shows date header with calendar icon (no "打設日" header)
 */
const PlanDeliveryDateGroup: React.FC<PlanDeliveryDateGroupProps> = ({ date, deliveries }) => {
  const formattedDate = formatDateToYYMMDD(date);

  return (
    <Box sx={{ width: '100%' }}>
      {/* Date Header with Calendar Icon */}
      <Box
        sx={{
          backgroundColor: 'rgba(0, 71, 186, 0.08)',
          height: '56px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          paddingX: '16px',
          paddingY: '8px',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', width: '100%' }}>
          {/* Calendar Icon */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '24px',
              height: '24px',
              flexShrink: 0,
            }}
          >
            <CalendarIcon sx={{ color: '#0047BA', fontSize: '24px' }} />
          </Box>

          {/* Date */}
          <Box sx={{ flex: 1 }}>
            <Typography sx={dateHeaderTypographyStyle} translate='no'>
              {formattedDate}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Divider */}
      <Divider sx={dividerStyle} />

      {/* Content - List of deliveries for this date */}
      <Box
        sx={{
          paddingX: '16px',
          paddingY: '12px',
          display: 'flex',
          flexDirection: 'column',
          gap: 0,
        }}
      >
        {deliveries.map((delivery, index) => {
          const fallbackId = `delivery-${index}`;
          const deliveryId = delivery.id || fallbackId;
          return (
            <React.Fragment key={deliveryId}>
              {index > 0 && (
                <Box
                  sx={{
                    paddingX: '16px',
                    paddingY: 0,
                  }}
                >
                  <Divider
                    sx={{
                      ...dividerStyle,
                      marginY: 3,
                    }}
                  />
                </Box>
              )}
              <Box
                id={`plan-delivery-${deliveryId}`}
                data-placement-date={delivery.placementDate}
                data-delivery-id={delivery.id}
              >
                <PlanDeliveryItem delivery={delivery} />
              </Box>
            </React.Fragment>
          );
        })}
      </Box>
    </Box>
  );
};

interface PlanDetailViewMobileProps {
  planDeliveries: CreatePlanRequest[];
  scrollToDeliveryId?: string; // Optional delivery ID to scroll to
}

/**
 * Mobile version of plan detail view
 * Groups plan deliveries by date and displays them in sections
 * Dates are sorted from oldest to newest
 */
export const PlanDetailViewMobile: React.FC<PlanDetailViewMobileProps> = ({
  planDeliveries,
  scrollToDeliveryId,
}) => {
  // Group deliveries by date and sort from oldest to newest
  const groupedByDate = React.useMemo(() => {
    if (!planDeliveries || planDeliveries.length === 0) {
      return [];
    }

    // Group by placementDate
    const grouped: Record<string, CreatePlanRequest[]> = {};
    planDeliveries.forEach((delivery) => {
      const date = delivery.placementDate;
      if (date) {
        if (!grouped[date]) {
          grouped[date] = [];
        }
        grouped[date].push(delivery);
      }
    });

    // Convert to array and sort by date (oldest to newest)
    const sortedDates = Object.keys(grouped).sort((a, b) => {
      return new Date(a).getTime() - new Date(b).getTime();
    });

    return sortedDates.map((date) => ({
      date,
      deliveries: grouped[date],
    }));
  }, [planDeliveries]);

  // Scroll to delivery when scrollToDeliveryId changes (mobile only)
  // IMPORTANT: Also depend on planDeliveries and groupedByDate to ensure data is loaded before scrolling
  useEffect(() => {
    // Only scroll if we have deliveries data and scrollToDeliveryId
    if (!scrollToDeliveryId || !planDeliveries || planDeliveries.length === 0) {
      return;
    }

    const elementId = `plan-delivery-${scrollToDeliveryId}`;

    // Check if the delivery exists in planDeliveries
    const deliveryExists = planDeliveries.some((d) => d.id === scrollToDeliveryId);

    if (!deliveryExists) {
      return;
    }

    // Try multiple times with increasing delay to ensure DOM is ready
    const tryScroll = (attempt: number = 1, maxAttempts: number = 10) => {
      const element = document.getElementById(elementId);

      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      } else if (attempt < maxAttempts) {
        setTimeout(() => tryScroll(attempt + 1, maxAttempts), attempt * 100);
      }
    };

    // Start trying after initial delay - wait a bit longer for DOM to be ready
    setTimeout(() => tryScroll(), 200);
  }, [scrollToDeliveryId, planDeliveries, groupedByDate]);

  if (groupedByDate.length === 0) {
    return null;
  }

  return (
    <MobileCardContainer headerTitle='打設日'>
      {/* Date Groups - Each date group only shows date header */}
      {groupedByDate.map((group, index) => (
        <React.Fragment key={group.date}>
          {index > 0 && <Divider sx={dividerStyle} />}
          <PlanDeliveryDateGroup date={group.date} deliveries={group.deliveries} />
        </React.Fragment>
      ))}
    </MobileCardContainer>
  );
};
